// All legal page content — kept in one place for easy review/updates.
// Each page renders inside <LegalPage /> via slug-based lookup.
//
// Format conventions:
//   - Plain text + simple markdown-like markers we render manually:
//     ## Heading, **bold**, - bullet, blank lines = paragraphs.
//   - Effective dates auto-update on every release; we hardcode for now
//     so legal counsel can review the exact text shipped.

const COMPANY = 'Backyard Entertainment and Lifestyle Private Limited';
const SHORT   = 'Backyard';
const ADDRESS = '8, Gitaneel Arcade Association, Hill Road, Bandra West, Mumbai - 400050, Maharashtra, India';
const GO_NAME = 'Atit P';
const GO_EMAIL = 'backyardcorporate@gmail.com';
const JURISDICTION = 'Courts of Mumbai, Maharashtra, India';
const MIN_AGE = 13;
const EFFECTIVE_DATE = 'April 30, 2026';

export const LEGAL_PAGES = {

  privacy: {
    title: 'Privacy Policy',
    short: 'How we collect, use, and protect your information.',
    body: `**Effective Date:** ${EFFECTIVE_DATE}

${COMPANY} (referred to as "${SHORT}", "we", "us" or "our") operates the Backyard mobile and web application (the "Service"). This Privacy Policy explains what personal information we collect, how we use and share it, and the choices and rights you have. We comply with India's Digital Personal Data Protection Act, 2023 (DPDPA) and applicable global privacy standards.

By using the Service, you agree to this Privacy Policy. If you do not agree, do not use the Service.

## 1. Information We Collect
**Information you provide directly:**
- Account data: name, email address, mobile number, password (stored as a salted hash by our authentication provider).
- Profile data: profile picture, bio, sport preferences, age, gender (optional).
- Payment metadata: order details, transaction IDs and refund history (we do NOT store card numbers, CVV, UPI handles or bank credentials — these are tokenised and held by our payment processor, Razorpay).
- Player & team data: stats, match performance, auction history, captaincy assignments.
- Content: photos, comments, ratings, support messages.

**Information we collect automatically:**
- Device & technical data: IP address, device model, operating system, browser type, app version, language, time zone.
- Usage data: pages visited, features used, click events, crash reports.
- Cookies & similar technologies: see our Cookie Policy.
- Approximate location (city-level) derived from IP. Precise GPS only with your explicit permission for venue navigation.
- Push notification tokens (FCM) when you opt in.

**Information from third parties:**
- Sign-in providers (Google) share your name, email and profile photo when you authenticate.
- Razorpay shares payment status callbacks.
- Resend processes our outbound transactional emails and reports delivery status.

## 2. How We Use Your Information
- Provide, operate, and improve the Service.
- Authenticate you and secure your account.
- Process bookings, registrations, payments, and refunds.
- Run tournaments — fixtures, live scoring, auctions, results.
- Send you transactional communications (booking confirmations, refunds, match alerts) and notifications you have opted in to.
- Enforce our Terms of Service and Community Guidelines, prevent fraud and abuse.
- Comply with legal obligations and respond to lawful requests.

## 3. Legal Basis (DPDPA & global)
We process your data on the following legal bases: your consent (notifications, optional profile fields), the necessity to perform our contract with you (account, bookings, payments), legitimate interests (security, fraud prevention, product improvement), and legal obligations (tax, accounting, regulatory).

## 4. Sharing Your Information
We do not sell your personal data. We share data only with:
- **Service providers** acting on our behalf under written agreements: Firebase (authentication, push, infrastructure), Razorpay (payments), Resend (email delivery), Cloudinary (image hosting), Google Maps (venue display).
- **Tournament organisers and team owners** where you have voluntarily registered for or played in their events — limited to data needed to run the event (name, role, team, performance stats).
- **Regulators, courts and law enforcement** when legally compelled.
- **Acquirers** in the event of a merger, sale, or restructuring, with notice to you.

## 5. International Transfers
Some of our service providers store data outside India (e.g., Firebase, Resend, Cloudinary). We rely on the providers' contractual safeguards and applicable cross-border transfer rules under the DPDPA.

## 6. Data Retention
We keep personal data only as long as needed for the purposes described, or as required by law. Account data is retained until you delete your account or after 24 months of inactivity. Transactional records (bookings, invoices) are retained for 8 years for tax and accounting compliance even after account deletion.

## 7. Your Rights
Subject to applicable law, you have the right to:
- Access a copy of your personal data
- Correct inaccurate data
- Request erasure / delete your account (in-app: Profile → Delete Account)
- Withdraw consent for non-essential processing (notifications, marketing)
- Lodge a complaint with the Data Protection Board of India

To exercise these rights, contact our Grievance Officer at ${GO_EMAIL}. We respond within 30 days.

## 8. Children
The Service is intended for users aged ${MIN_AGE} and above. Users under 18 require verifiable consent from a parent or guardian for paid features.

## 9. Security
We use industry-standard safeguards: encrypted transit (TLS), encrypted storage, hashed passwords, role-based access controls, audit logs, and regular security reviews. No system is perfectly secure; we will notify affected users without undue delay if a breach materially impacts their data.

## 10. Changes to this Policy
We may update this Privacy Policy from time to time. We will notify you of material changes via email or an in-app banner at least 7 days before the change takes effect. Continued use after the effective date constitutes acceptance.

## 11. Grievance Officer
**${GO_NAME}**, Grievance Officer
${COMPANY}
${ADDRESS}
Email: ${GO_EMAIL}

We acknowledge complaints within 48 hours and resolve them within 30 days.`,
  },

  terms: {
    title: 'Terms of Service',
    short: 'The agreement governing your use of Backyard.',
    body: `**Effective Date:** ${EFFECTIVE_DATE}

These Terms of Service ("Terms") form a binding agreement between you and ${COMPANY} ("${SHORT}", "we", "us"). They govern your use of the Backyard mobile and web application, websites, and related services (collectively, the "Service"). By creating an account or using the Service you accept these Terms.

## 1. Eligibility
You must be at least ${MIN_AGE} years old. If you are between ${MIN_AGE} and 18, you confirm a parent or legal guardian has reviewed and consented to these Terms. You must have the legal capacity to enter into a binding contract.

## 2. Your Account
- You agree to provide accurate, current, and complete information.
- You are responsible for all activity under your account and must keep your password and authentication factors confidential.
- Notify us immediately at ${GO_EMAIL} of any unauthorised access.
- We may suspend or terminate accounts that violate these Terms or applicable law.

## 3. Permitted Use
You may use the Service to discover events, register for tournaments, book tickets, manage teams, participate in auctions, view live scoring, purchase merchandise, and engage with the community. You must NOT:
- Use the Service for unlawful, fraudulent, or harmful purposes.
- Impersonate any person or misrepresent your affiliation.
- Bypass, disable, or interfere with security or rate-limiting features.
- Scrape, copy, or systematically download Service data without permission.
- Upload or transmit unlawful, defamatory, obscene, infringing, or hateful content.
- Use the Service to harass, threaten, or harm others (see Community Guidelines).

## 4. User Content
You retain ownership of the content you submit (photos, comments, profile information). By submitting content, you grant ${SHORT} a worldwide, royalty-free, sublicensable licence to host, store, reproduce, modify, display, and distribute that content solely to operate, improve, and promote the Service. You represent that you have all rights necessary to grant this licence and that the content does not violate any third-party rights or applicable laws.

## 5. Tournaments, Bookings, Payments
- Event organisers ("Organisers") publish tournaments, fixtures, and bookings on the Service. ${SHORT} acts as a platform; the contract for an event is between you and the Organiser.
- All payments are processed via Razorpay. By making a payment you also accept Razorpay's terms.
- Prices are displayed inclusive of applicable taxes unless stated otherwise. Prices are in Indian Rupees (₹) unless stated.
- Refunds are governed by our Refund & Cancellation Policy.

## 6. Auctions
Live auctions are run by Organisers using our auction tools. Bids are binding once submitted; reverting a sold player is at the discretion of the Organiser/auctioneer and may carry consequences (squad-count adjustments, wallet refunds). You are responsible for understanding the rules of any auction you participate in before bidding.

## 7. Memberships
Optional paid memberships are subscription products. They auto-renew unless cancelled before the renewal date. Refund eligibility on memberships follows our Refund & Cancellation Policy.

## 8. Intellectual Property
The Service — including all software, design, text, graphics, logos, and trademarks (other than user content) — is owned by ${SHORT} or its licensors and protected by Indian and international IP laws. We grant you a limited, non-exclusive, non-transferable, revocable licence to use the Service for personal, non-commercial purposes consistent with these Terms.

## 9. Third-Party Services
The Service uses third-party services including Firebase, Razorpay, Resend, Cloudinary, and Google Maps. We are not responsible for the acts or omissions of these third parties.

## 10. Disclaimers
THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, TO THE MAXIMUM EXTENT PERMITTED BY LAW. ${SHORT} DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY EVENT, MATCH, OR AUCTION WILL TAKE PLACE AS SCHEDULED.

Sport involves physical risk. ${SHORT} is not responsible for injuries, accidents, or property loss arising from your participation in any event.

## 11. Limitation of Liability
To the maximum extent permitted by law, ${SHORT}'s aggregate liability for any claim arising out of or related to the Service shall not exceed the amount you paid to ${SHORT} in the 12 months preceding the claim, or ₹10,000 — whichever is greater. ${SHORT} shall not be liable for indirect, incidental, special, consequential, or punitive damages.

## 12. Indemnification
You agree to indemnify and hold harmless ${SHORT}, its directors, employees, and agents from any claim, demand, or damages arising from your breach of these Terms, your User Content, or your violation of any rights of another.

## 13. Suspension and Termination
We may suspend or terminate your access at any time, with or without notice, if we reasonably believe you have violated these Terms. You may terminate your account at any time via Profile → Delete Account. Termination does not relieve either party of obligations accrued prior to termination.

## 14. Changes
We may modify these Terms. Material changes will be notified at least 7 days in advance via email or in-app banner. Continued use after the change effective date is acceptance.

## 15. Governing Law and Disputes
These Terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of the ${JURISDICTION}. Before filing a claim, you agree to first contact us at ${GO_EMAIL} and attempt good-faith resolution for at least 30 days.

## 16. Contact
${COMPANY}
${ADDRESS}
Email: ${GO_EMAIL}`,
  },

  refund: {
    title: 'Refund & Cancellation Policy',
    short: 'When and how refunds are processed.',
    body: `**Effective Date:** ${EFFECTIVE_DATE}

This policy explains when and how refunds are issued for purchases made on the ${SHORT} platform. It applies to event tickets, tournament registrations, shop purchases, and memberships.

## 1. Event Tickets and Tournament Registrations
- **More than 7 days before the event:** Full refund minus a 5% processing fee.
- **48 hours to 7 days before the event:** 50% refund.
- **Less than 48 hours before the event:** No refund, except where the event is cancelled or rescheduled by the Organiser.
- **Event cancelled by the Organiser:** Full refund within 7 business days.
- **Event rescheduled by the Organiser:** You may either keep your booking for the new date or request a full refund within 7 days of the rescheduling notice.

Specific events may publish their own refund terms which override the defaults above; the event-specific terms will be displayed at checkout.

## 2. Shop Purchases (physical and digital products)
- **Cancellation before dispatch:** Full refund.
- **Returns within 7 days of delivery:** Eligible for refund only if the product is unused, in original packaging, and the issue is on our side (wrong item, defective, damaged in transit). Personal-use items, including food, perishables, custom-printed merchandise and innerwear, are not returnable.
- **Restocking fee:** A 10% restocking fee may apply for change-of-mind returns at our discretion.

## 3. Memberships
- **Within 7 days of purchase:** Full refund if no member benefits have been used (no event registrations, no exclusive content accessed, no auctioneer logins issued).
- **After 7 days or after benefits used:** Non-refundable; you retain access until the end of the paid term.
- **Auto-renewal:** You can cancel auto-renewal anytime via Profile → Membership; the cancellation takes effect at the end of the current billing period.

## 4. How to Request a Refund
1. Open the booking, order or membership in the app.
2. Tap "Request Refund" (where available) or email ${GO_EMAIL} with your booking ID and reason.
3. Refunds are issued to the original payment method via Razorpay within 5–7 business days of approval. Bank or card timelines may add up to 7 additional days.

## 5. Disputes
If you believe a refund decision is incorrect, escalate to our Grievance Officer (${GO_NAME}, ${GO_EMAIL}). Final disputes are governed by Indian consumer protection law and the ${JURISDICTION}.

## 6. Fraud and Abuse
We reserve the right to refuse refunds where we identify fraudulent activity, abuse of the refund policy (e.g., serial cancellations), or where the booking was used in part. Such decisions may include suspending the account.

## 7. Currency and Taxes
All refunds are in the original currency (Indian Rupees, ₹). Taxes (if collected) are refunded along with the principal where applicable. Razorpay processing fees, if any, may be deducted depending on the merchant agreement.

## Contact
${COMPANY}
${ADDRESS}
Email: ${GO_EMAIL}`,
  },

  'account-deletion': {
    title: 'Account Deletion',
    short: 'How to delete your account and what happens to your data.',
    body: `**Effective Date:** ${EFFECTIVE_DATE}

You have the right to delete your ${SHORT} account at any time. This page explains how, what is deleted, and what we are required to retain.

## How to Delete Your Account

**In-app (recommended):**
1. Open the ${SHORT} app.
2. Go to **Profile → Delete Account**.
3. Confirm the action.

Your account is queued for deletion immediately. You will be signed out and can no longer log in with the same credentials.

**By email (alternate):**
If you cannot access the app, email ${GO_EMAIL} from your registered email address with the subject "Account Deletion Request". We will verify your identity and complete the deletion within 7 business days.

## What Gets Deleted Immediately
- Your name, email, mobile number, profile picture, bio, and personal preferences.
- Notification preferences and push notification tokens.
- Saved payment methods (we don't store card data — Razorpay holds tokens; you should also revoke from Razorpay if applicable).
- Bookings, registrations, and tournament participation history are anonymised: your name and contact details are removed; the booking record itself is retained for accounting (see below).

## What We Retain (and why)
- **Transactional records** (bookings, invoices, refund history): retained for 8 years to comply with the Income Tax Act and other Indian financial regulations.
- **Tournament and match records** (scores, fixtures, public results): retained as part of the public sporting record. Your personal name is removed; an anonymised identifier replaces it.
- **Audit logs** (security events, fraud signals): retained for 12 months for security purposes.
- **Backups**: deleted data may persist in encrypted backups for up to 90 days, after which it is permanently purged.

## What You Cannot Recover
- Your account profile and preferences.
- Saved drafts, watchlists, and personal lists.
- Membership benefits (no refund for unused term unless the deletion is within 7 days of purchase — see Refund Policy).

## Data Export Before Deletion
Before deleting, you may request a copy of your personal data by emailing ${GO_EMAIL}. We will provide it in a portable format (JSON) within 30 days.

## Re-creating an Account
You may create a new account in the future using the same email address. The new account will not have any history from the deleted account.

## Contact
${COMPANY}
${ADDRESS}
Grievance Officer: ${GO_NAME} — ${GO_EMAIL}`,
  },

  'community-guidelines': {
    title: 'Community Guidelines',
    short: 'How we expect everyone to behave on Backyard.',
    body: `**Effective Date:** ${EFFECTIVE_DATE}

${SHORT} brings together players, organisers, captains, fans, and everyone in between. These Community Guidelines describe what's expected and what's prohibited. They apply to all interactions on the Service — comments, profile content, in-app messages, support tickets, and conduct at events organised through the platform.

## What's Expected
- **Respect** other users, organisers, opponents, umpires, and staff.
- **Play fair**: follow event and auction rules; report rule-breaking instead of retaliating.
- **Be honest**: use your real name and accurate stats; don't impersonate anyone.
- **Be safe**: protect your password; don't share account access.

## What's Prohibited
We do not allow content or behaviour that:
- Harasses, bullies, threatens, or stalks others.
- Promotes hate or discrimination based on race, religion, caste, gender, sexual orientation, disability, or nationality.
- Sexualises minors or depicts non-consensual content.
- Encourages violence or self-harm.
- Promotes betting, match-fixing, or gambling outside legal frameworks.
- Engages in cheating, ghost players, fake stats, or auction manipulation.
- Spams, scams, phishes, or distributes malware.
- Infringes intellectual property rights (logos, photos, music).
- Reveals private personal information of another person without consent.
- Solicits paid services unrelated to the platform inside community spaces.

## Reporting & Moderation
If you see content or behaviour that violates these Guidelines:
- Use the in-app **Report** option (where available).
- Email ${GO_EMAIL} with the user, content, and a description.

We review every report. Action depends on severity and history:
1. **Warning** — informal first contact for minor violations.
2. **Content removal** — offending posts/photos are taken down.
3. **Temporary suspension** — typically 7–30 days for repeated or serious violations.
4. **Permanent ban** — for severe violations (sexualising minors, doxxing, fraud) or repeated suspensions.
5. **Forfeiture** — auction picks, match results, or tournament rankings may be voided where competitive integrity is compromised.

We may report unlawful activity to law enforcement.

## Appeals
If you believe a moderation decision was wrong, reply to the email notifying you of the decision. The Grievance Officer will review within 14 days. Their decision is final unless inconsistent with applicable law.

## Children's Safety
Users under 18 require parental consent. We strictly prohibit any behaviour that endangers minors. Suspected child safety violations are reported to authorities immediately.

## Contact
${GO_NAME}, Grievance Officer
${COMPANY}
${ADDRESS}
Email: ${GO_EMAIL}`,
  },

  'shipping-delivery': {
    title: 'Shipping & Delivery Policy',
    short: 'How shop orders are shipped and delivered.',
    body: `**Effective Date:** ${EFFECTIVE_DATE}

This policy applies to physical merchandise purchased via the ${SHORT} shop. Digital products (vouchers, memberships, e-tickets) are delivered electronically and not covered here.

## 1. Delivery Locations
We currently ship within India. Cross-border shipping may be added in the future and announced in-app.

## 2. Processing Time
- Orders are typically processed within 1–3 business days of payment confirmation.
- Custom or made-to-order items (e.g., team jerseys with names/numbers) take 5–10 business days to manufacture before dispatch.
- You will receive a notification with a tracking number when the order ships.

## 3. Estimated Delivery Times
- **Metro cities** (Mumbai, Delhi, Bengaluru, Chennai, Kolkata, Hyderabad, Pune): 2–5 business days post-dispatch.
- **Other locations**: 4–10 business days post-dispatch.
- **Remote / Northeast / Andaman & Nicobar / Lakshadweep**: 7–14 business days.

These are estimates. Actual delivery may vary due to courier service, weather, or holidays.

## 4. Shipping Fees
- Shipping fees, where applicable, are displayed at checkout before payment.
- Free shipping promotions are clearly indicated on product or cart pages.

## 5. Tracking
Once dispatched you will receive a tracking link via email and in-app notification. You can also view the tracking status in **Profile → Orders**.

## 6. Delivery Issues
- **Order not received within the estimated window:** contact ${GO_EMAIL} with your order ID. We will investigate with the courier and either re-ship, refund, or assist as per the situation.
- **Damaged in transit:** photograph the package and the damaged product within 48 hours of delivery and email us. We arrange a return pickup.
- **Wrong item received:** email us within 7 days of delivery. We arrange replacement at no cost.

## 7. Failed Delivery / Return to Origin
If a package is returned undelivered (incorrect address, recipient unavailable, refused), we attempt one re-dispatch at the customer's request. If a second re-dispatch is also unsuccessful, the order may be cancelled and refunded minus the courier fees actually incurred.

## 8. Refunds and Returns
Returns and refunds are governed by our Refund & Cancellation Policy.

## Contact
${COMPANY}
${ADDRESS}
Support: ${GO_EMAIL}`,
  },

  cookies: {
    title: 'Cookie & Tracking Policy',
    short: 'Cookies, local storage, and similar technologies we use.',
    body: `**Effective Date:** ${EFFECTIVE_DATE}

This policy explains how ${SHORT} uses cookies, local storage, and similar tracking technologies on our website and mobile applications. This policy supplements our Privacy Policy.

## 1. What Are Cookies?
Cookies are small text files placed on your device by websites you visit. We also use related technologies including localStorage, sessionStorage, and IndexedDB on web, and equivalent secure storage on mobile.

## 2. Categories We Use

**Strictly Necessary** — required for the Service to work; cannot be disabled:
- Authentication tokens (Firebase Auth).
- Session identifiers and CSRF tokens.
- Service worker registration for offline support and push notifications.
- Cookie consent state itself.

**Functional** — remember your choices:
- Theme/language preferences.
- Last-viewed event or filter selections.
- Notification permission status snapshot.

**Analytics & Performance** — help us improve the Service:
- Aggregated, pseudonymised usage data (page views, click events, error reports).
- Performance metrics (page load time, API latency).
- These do NOT track you across other websites.

**Push Notifications** — only if you opt in:
- Firebase Cloud Messaging (FCM) registration token.
- Service worker for delivering push messages when the app is in the background.

**Third-Party Cookies** set by services we use:
- **Firebase** — authentication & messaging. Privacy: https://firebase.google.com/support/privacy
- **Razorpay** — payment session and fraud detection (only on checkout pages). Privacy: https://razorpay.com/privacy
- **Google Maps** — venue display. Privacy: https://policies.google.com/privacy

We do not use cookies for behavioural advertising or third-party advertising networks.

## 3. Managing Cookies
- **Browser settings:** Most browsers let you block or delete cookies. Disabling Strictly Necessary cookies will break login.
- **Notification permission:** Toggle from your browser's site settings or in-app under Profile → Notification Preferences.
- **Mobile:** OS-level settings let you reset advertising IDs and clear app data.

## 4. Do Not Track
We do not currently respond to Do Not Track signals because there is no industry consensus on how to interpret them. We minimise tracking by default as outlined above.

## 5. Children
We do not knowingly use cookies to target advertisements at users under 18.

## 6. Changes
We will update this policy as we add or remove cookie categories and notify users via the website or in-app banner.

## Contact
${COMPANY}
${ADDRESS}
Email: ${GO_EMAIL}`,
  },

  grievance: {
    title: 'Grievance Redressal',
    short: 'How to raise a complaint and our resolution timelines.',
    body: `**Effective Date:** ${EFFECTIVE_DATE}

In compliance with the Digital Personal Data Protection Act, 2023, the Information Technology Act, 2000 (and Rules), and the Consumer Protection (E-commerce) Rules, 2020, ${COMPANY} has appointed a Grievance Officer to resolve user complaints.

## Grievance Officer
**Name:** ${GO_NAME}
**Designation:** Grievance Officer
**Address:** ${ADDRESS}
**Email:** ${GO_EMAIL}
**Working hours:** Monday to Friday, 10:00 to 18:00 IST (excluding public holidays).

## What You Can Raise
You may file a grievance about:
- Processing, accuracy, or deletion of your personal data.
- Misuse of your account or content.
- Disputed transactions, refund delays, or order issues.
- Content posted by other users that violates our Community Guidelines or applicable law.
- Any other concern about the Service.

## How to File
1. Email ${GO_EMAIL} with the subject "Grievance: <topic>".
2. Include: your name, registered email or mobile, a clear description, and any supporting evidence (screenshots, order IDs).
3. We accept grievances in English or Hindi.

## Acknowledgement & Resolution Timeline
- **Acknowledgement:** within **48 hours** of receipt.
- **Resolution:** within **30 calendar days** of receipt.
- For complex cases requiring third-party coordination (e.g., banks, couriers), we will share interim updates.

## Escalation
If your grievance is not satisfactorily resolved, you may escalate:
- **For data protection matters**, to the **Data Protection Board of India** under the DPDPA, 2023.
- **For consumer disputes**, to the **National Consumer Helpline** (https://consumerhelpline.gov.in) or the appropriate consumer forum.
- **For payment disputes**, also to **Razorpay support** at care@razorpay.com or your bank's grievance redressal officer.

## Records
We maintain a register of all grievances received and their resolution status, available for inspection on lawful request from regulators.

## Contact
${COMPANY}
${ADDRESS}
Grievance Officer: ${GO_NAME} — ${GO_EMAIL}`,
  },
};

export const LEGAL_NAV = [
  { slug: 'privacy',              label: 'Privacy Policy' },
  { slug: 'terms',                label: 'Terms of Service' },
  { slug: 'refund',               label: 'Refund Policy' },
  { slug: 'account-deletion',     label: 'Account Deletion' },
  { slug: 'community-guidelines', label: 'Community Guidelines' },
  { slug: 'shipping-delivery',    label: 'Shipping & Delivery' },
  { slug: 'cookies',              label: 'Cookies' },
  { slug: 'grievance',            label: 'Grievance Redressal' },
];
